import {rootState} from '@mintdata-wsp/common/state/RootState';
import {VisualComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualComponent';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {
    SelectionInteractionMode,
    SurfaceBounds,
    SurfaceSize
} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {FocusedArea} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/WorkbenchState';
import {KeyboardController} from '@mintdata-wsp/wsp/controllers/visualDesigner/workbench/KeyboardController';
import {BoxReparentingDisabledOn} from '@mintdata-wsp/wsp/controllers/visualDesigner/workbench/surface/SurfaceBoxReparentingController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';

export class SurfaceKeyboardController
{
    public static readonly KEY_MAP = {
        _enablePan: {sequence: 'space', action: 'keydown'},
        _disablePan: {sequence: 'space', action: 'keyup'},
        _enableBoxReparenting: {sequence: 'mod', action: 'keydown'},
        _disableBoxReparenting: {sequence: 'mod', action: 'keyup'},
        _groupIntoBox: ['mod+g'],
        _ungroupFromBox: ['mod+shift+g'],
        _copyBoxes: ['mod+c'],
        _pasteBoxes: ['mod+v'],
        _removeSelectedComponents: ['del', 'backspace'],
        _moveSelectedComponents: [
            'up', 'down', 'left', 'right',
            'shift+up', 'shift+down', 'shift+left', 'shift+right'
        ],
        _resizeSelectedComponents: [
            'mod+up', 'mod+down', 'mod+left', 'mod+right',
            'mod+shift+up', 'mod+shift+down', 'mod+shift+left', 'mod+shift+right'
        ],
        _enableDrawingComponent: ['r'],
        _enableDrawingComponentText: ['t'],

        // _escapeBoxReparenting: ['mod+esc', 'esc'],
        // _escapeDrawingComponent: ['esc']
        _escape: ['esc'],
        _escapeBoxReparenting: ['mod+esc'],
    };

    public static readonly KEY_HANDLERS = {};

    public init():void
    {
        this._initKeyHandlersMap();
    }

    public getKeyMap():any
    {
        return SurfaceKeyboardController.KEY_MAP;
    }

    public getKeyHandlers():any
    {
        return SurfaceKeyboardController.KEY_HANDLERS;
    }

    public handleSurfaceMouseEnter = () =>
    {
        const {surfaceArea} = controllers.ui;

        surfaceArea.disablePan();
    };

    public handleSurfaceFocus = () =>
    {
        const {workbench} = controllers.ui;

        workbench.setFocusedArea(FocusedArea.Surface);
    };

    private _initKeyHandlersMap()
    {
        const keyHandlers = SurfaceKeyboardController.KEY_HANDLERS;
        const keyMap = SurfaceKeyboardController.KEY_MAP;

        for( const funcName in keyMap )
        {
            const keyHandler = this[funcName];

            if( !keyHandler )
                console.error(`Could not find keyboard handler for ${funcName}`);

            keyHandlers[funcName] = KeyboardController.ignoreTags(keyHandler);
        }
    }

    private _enablePan = () =>
    {
        const {surfaceArea} = controllers.ui;

        const {surfaceSelection} = controllers.ui;
        const selectionMode:SelectionInteractionMode = surfaceSelection.getSelectionInteractionMode();

        if( surfaceSelection.isSelectionMoving(selectionMode) )
            return;

        const {surfaceDrawingComponent} = controllers.ui;

        if( surfaceDrawingComponent.isDrawingComponentEnabled() )
            return;

        surfaceArea.enablePan();
    };

    private _disablePan = () =>
    {
        const {surfaceArea} = controllers.ui;

        surfaceArea.disablePan();
    };

    private _enableBoxReparenting = () =>
    {
        const {surfaceSelection, surfaceBoxReparenting} = controllers.ui;

        const selectionMode:SelectionInteractionMode = surfaceSelection.getSelectionInteractionMode();

        if( surfaceSelection.isSelectionMoving(selectionMode) )
            surfaceBoxReparenting.enableBoxReparenting();

    };

    private _disableBoxReparenting = () =>
    {
        const {surfaceSelection, surfaceBoxReparenting} = controllers.ui;

        const selectionMode:SelectionInteractionMode = surfaceSelection.getSelectionInteractionMode();

        if( surfaceBoxReparenting.isBoxReparentingEnabled() && selectionMode === SelectionInteractionMode.StartMoving )
            surfaceBoxReparenting.disableBoxReparenting(BoxReparentingDisabledOn.ShortcutUp);
    };

    private _escapeBoxReparenting = () =>
    {
        const {surfaceBoxReparenting} = controllers.ui;

        if( surfaceBoxReparenting.isBoxReparentingEnabled() )
        {
            surfaceBoxReparenting.disableBoxReparenting(BoxReparentingDisabledOn.Esc);
            return true;
        }
    };

    private _groupIntoBox = (event:KeyboardEvent) =>
    {
        const {surfaceArea} = controllers.ui;

        if( surfaceArea.isGroupActionAvailable() )
        {
            surfaceArea.groupSelectedBoxesIntoBox();
        }

        event.preventDefault();
    };

    private _ungroupFromBox = (event:KeyboardEvent) =>
    {
        const {surfaceArea} = controllers.ui;

        if( surfaceArea.isUngroupActionAvailable() )
        {
            surfaceArea.ungroupSelectedBox();
        }

        event.preventDefault();
    };

    private _copyBoxes = () =>
    {
        const {surfaceArea} = controllers.ui;
        surfaceArea.copyBoxesToClipboard();
    };

    private _pasteBoxes = () =>
    {
        const {surfaceArea} = controllers.ui;
        surfaceArea.pasteBoxesFromClipboard();
    };

    private _removeSelectedComponents = () =>
    {
        const {surfaceArea} = controllers.ui;

        if( surfaceArea.isDeleteActionAvailable() )
        {
            surfaceArea.removeSelectedVisualComponents();
        }
    };

    private _moveSelectedComponents = (event:KeyboardEvent) =>
    {
        const {surfaceArea} = controllers.ui;

        event.preventDefault();

        switch( event.code )
        {
            case 'ArrowUp':
                event.shiftKey
                    ? surfaceArea.moveSelectedBoxesBy(SurfaceBounds.from(0, -10, 0, 0))
                    : surfaceArea.moveSelectedBoxesBy(SurfaceBounds.from(0, -1, 0, 0));
                break;

            case 'ArrowDown':
                event.shiftKey
                    ? surfaceArea.moveSelectedBoxesBy(SurfaceBounds.from(0, 10, 0, 0))
                    : surfaceArea.moveSelectedBoxesBy(SurfaceBounds.from(0, 1, 0, 0));
                break;

            case 'ArrowLeft':
                event.shiftKey
                    ? surfaceArea.moveSelectedBoxesBy(SurfaceBounds.from(-10, 0, 0, 0))
                    : surfaceArea.moveSelectedBoxesBy(SurfaceBounds.from(-1, 0, 0, 0));
                break;

            case 'ArrowRight':
                event.shiftKey
                    ? surfaceArea.moveSelectedBoxesBy(SurfaceBounds.from(10, 0, 0, 0))
                    : surfaceArea.moveSelectedBoxesBy(SurfaceBounds.from(1, 0, 0, 0));
                break;
        }
    };

    private _resizeSelectedComponents = (event:KeyboardEvent) =>
    {
        const {surfaceArea, surfaceSelection} = controllers.ui;

        event.preventDefault();

        if( event.metaKey )
        {
            const selectedBoxes:Box[] = surfaceSelection.getSelectedBoxes();

            switch( event.code )
            {
                case 'ArrowUp':
                    event.shiftKey
                        ? surfaceArea.resizeBoxesBy(selectedBoxes, new SurfaceSize(0, -10))
                        : surfaceArea.resizeBoxesBy(selectedBoxes, new SurfaceSize(0, -1));
                    break;

                case 'ArrowDown':
                    event.shiftKey
                        ? surfaceArea.resizeBoxesBy(selectedBoxes, new SurfaceSize(0, 10))
                        : surfaceArea.resizeBoxesBy(selectedBoxes, new SurfaceSize(0, 1));
                    break;

                case 'ArrowLeft':
                    event.shiftKey
                        ? surfaceArea.resizeBoxesBy(selectedBoxes, new SurfaceSize(-10, 0))
                        : surfaceArea.resizeBoxesBy(selectedBoxes, new SurfaceSize(-1, 0));
                    break;

                case 'ArrowRight':
                    event.shiftKey
                        ? surfaceArea.resizeBoxesBy(selectedBoxes, new SurfaceSize(10, 0))
                        : surfaceArea.resizeBoxesBy(selectedBoxes, new SurfaceSize(1, 0));
                    break;
            }
        }
    };

    private _enableDrawingComponent = (event:KeyboardEvent) =>
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;

        if( area.panModeState.isEnabled ) return;

        const {surfaceArea, surfaceDrawingComponent} = controllers.ui;

        if( surfaceDrawingComponent.isDrawingComponentEnabled() )
            surfaceDrawingComponent.disableDrawingComponent();
        else
        {
            let component:VisualComponent;

            switch( event.code )
            {
                case 'KeyR':
                    component = surfaceArea.findBoxVisualComponent();
                    break;

                default:
                    component = surfaceArea.findBoxVisualComponent();
            }

            surfaceDrawingComponent.enableDrawingComponent(component);
        }

        event.preventDefault();
    };

    private _enableDrawingComponentText = (event:KeyboardEvent) =>
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;

        if( area.panModeState.isEnabled ) return;

        const {surfaceArea, surfaceDrawingComponent} = controllers.ui;

        if( surfaceDrawingComponent.isDrawingComponentEnabled() )
            surfaceDrawingComponent.disableDrawingComponent();
        else
        {
            let component:VisualComponent;

            switch( event.code )
            {
                case 'KeyT':
                    component = surfaceArea.findTextVisualComponent();
                    break;

                default:
                    component = surfaceArea.findTextVisualComponent();
            }

            surfaceDrawingComponent.enableDrawingComponent(component);
        }

        event.preventDefault();
    };

    private _escapeDrawingComponent = () =>
    {
        const {surfaceDrawingComponent} = controllers.ui;

        if( surfaceDrawingComponent.isDrawingComponentEnabled() )
        {
            surfaceDrawingComponent.disableDrawingComponent();
            return true;
        }
    };

    private _escape = () =>
    {
        return this._escapeBoxReparenting() || this._escapeDrawingComponent();
    };
}
